// src/layouts/AppLayout.tsx
import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from '../components/navigation/Sidebar';
import Header from '../components/navigation/Header';
import BottomNavigation from '../components/navigation/BottomNavigation';
import { OfflineIndicator } from '../components/common/OfflineIndicator';
import { UpdateBanner } from '../components/common/UpdateBanner';
import { HeaderProvider, useHeaderContext } from '../contexts/HeaderContext';
import { TodayCalendarProvider } from '../contexts/TodayCalendarContext';
import { ScheduleGeneratorProvider } from '../features/scheduleGenerator';
import { useOnboarding, Lesson3Guide } from '../features/onboarding';

const AppLayoutContent: React.FC<{ currentPage: string }> = ({ currentPage }) => {
  const { config } = useHeaderContext();
  const { lesson3GuideState, closeLesson3Guide } = useOnboarding();
  const isOnTodayPage = currentPage === 'today';

  // 定義需要全屏高度且自行處理滾動的 "App 模式" 頁面
  const isAppPage = ['calendar'].includes(currentPage);

  // 根據頁面類型決定容器樣式
  const containerClasses = isAppPage
    ? 'h-full'
    : `${config?.containerMaxWidth || 'max-w-7xl'} mx-auto`;

  const renderContent = () => (
    <>
      <Header />

      {/* Sub Header - 只在有設置時顯示 */}
      {config?.subHeader && (
        <div>{config.subHeader}</div>
      )}

      <main className={`flex-1 bg-surface-page ${isAppPage ? 'overflow-hidden' : 'overflow-x-hidden overflow-y-auto hide-scrollbar pb-16 md:pb-0'}`}>
        <div className={containerClasses}>
          <Outlet />
        </div>
      </main>
    </>
  );

  return (
    <div className="flex h-screen bg-surface-page overflow-hidden">
      {/* 桌面版側邊欄 */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 條件性包裝：只在 Today 頁面使用共享數據 */}
        {isOnTodayPage ? (
          <TodayCalendarProvider>
            {renderContent()}
          </TodayCalendarProvider>
        ) : (
          renderContent()
        )}

        {/* 行動版底部導航 - 只在小尺寸顯示 */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-navigation">
          <BottomNavigation currentPage={currentPage} />
        </div>
      </div>

      {/* Lesson 3 引導（只在 goals 相關頁面顯示） */}
      {lesson3GuideState && currentPage === 'goals' && (
        <Lesson3Guide
          onClose={closeLesson3Guide}
        />
      )}
    </div>
  );
};

const AppLayout: React.FC = () => {
  const location = useLocation();
  const currentPage = location.pathname.split('/')[1] || 'today';

  return (
    <HeaderProvider>
      <ScheduleGeneratorProvider>
        <AppLayoutContent currentPage={currentPage} />
        {/* 行動版 / 桌面版更新提示分別由 UpdateBanner / Sidebar 各自處理 */}
        <UpdateBanner />
        {/* 離線狀態指示器 - PWA 離線優先體驗 */}
        <OfflineIndicator position="top" />
      </ScheduleGeneratorProvider>
    </HeaderProvider>
  );
};

export default AppLayout;